/**
 * Validated Fetch Wrapper
 *
 * Wrapper around fetch that automatically validates responses using Zod schemas.
 * Provides type-safe API calls with runtime validation.
 */

import { z } from "zod";
import { validateApiResponse, validateApiArrayResponse, type ValidationResult } from "../validation/api-validator";

// ============================================================================
// FETCH OPTIONS
// ============================================================================

export interface ValidatedFetchOptions extends RequestInit {
  /**
   * JWT token for authentication
   */
  jwt?: string;

  /**
   * Context for better error messages
   */
  context?: string;

  /**
   * Log validation errors
   */
  logErrors?: boolean;
}

// ============================================================================
// VALIDATED FETCH FUNCTIONS
// ============================================================================

/**
 * Fetch and validate a single item response
 *
 * @example
 * const result = await fetchValidated(
 *   AgentSchema,
 *   '/api/agents/123',
 *   { jwt: token, context: 'Agent' }
 * );
 *
 * if (result.success) {
 *   console.log(result.data); // Type-safe Agent
 * }
 */
export async function fetchValidated<T>(
  schema: z.ZodSchema<T>,
  url: string,
  options: ValidatedFetchOptions = {},
): Promise<ValidationResult<T>> {
  const { jwt, context, logErrors, ...fetchOptions } = options;

  try {
    // Prepare headers
    const headers = new Headers(fetchOptions.headers);
    if (jwt) {
      headers.set("Authorization", `Bearer ${jwt}`);
    }
    if (!headers.has("Content-Type") && (fetchOptions.method === "POST" || fetchOptions.method === "PUT")) {
      headers.set("Content-Type", "application/json");
    }

    // Make request
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // Check HTTP status
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    // Parse JSON
    const data = await response.json();

    // Validate response
    return validateApiResponse(schema, data, { context, logErrors });
  } catch (error) {
    const errorMessage = context
      ? `Failed to fetch ${context}: ${error instanceof Error ? error.message : "Unknown error"}`
      : `Fetch failed: ${error instanceof Error ? error.message : "Unknown error"}`;

    if (logErrors !== false) {
      console.error("[Validated Fetch]", errorMessage, error);
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * Fetch and validate an array response
 *
 * @example
 * const result = await fetchValidatedArray(
 *   AgentSchema,
 *   '/api/agents',
 *   { jwt: token, context: 'Agents list' }
 * );
 *
 * if (result.success) {
 *   console.log(result.data); // Type-safe Agent[]
 * }
 */
export async function fetchValidatedArray<T>(
  schema: z.ZodSchema<T>,
  url: string,
  options: ValidatedFetchOptions = {},
): Promise<ValidationResult<T[]>> {
  const { jwt, context, logErrors, ...fetchOptions } = options;

  try {
    // Prepare headers
    const headers = new Headers(fetchOptions.headers);
    if (jwt) {
      headers.set("Authorization", `Bearer ${jwt}`);
    }
    if (!headers.has("Content-Type") && (fetchOptions.method === "POST" || fetchOptions.method === "PUT")) {
      headers.set("Content-Type", "application/json");
    }

    // Make request
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // Check HTTP status
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    // Parse JSON
    const data = await response.json();

    // Validate response
    return validateApiArrayResponse(schema, data, { context, logErrors });
  } catch (error) {
    const errorMessage = context
      ? `Failed to fetch ${context}: ${error instanceof Error ? error.message : "Unknown error"}`
      : `Fetch failed: ${error instanceof Error ? error.message : "Unknown error"}`;

    if (logErrors !== false) {
      console.error("[Validated Fetch]", errorMessage, error);
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
}

/**
 * POST request with validation
 */
export async function postValidated<T>(
  schema: z.ZodSchema<T>,
  url: string,
  body: unknown,
  options: ValidatedFetchOptions = {},
): Promise<ValidationResult<T>> {
  return fetchValidated(schema, url, {
    ...options,
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * POST request with array validation
 */
export async function postValidatedArray<T>(
  schema: z.ZodSchema<T>,
  url: string,
  body: unknown,
  options: ValidatedFetchOptions = {},
): Promise<ValidationResult<T[]>> {
  return fetchValidatedArray(schema, url, {
    ...options,
    method: "POST",
    body: JSON.stringify(body),
  });
}

/**
 * PUT request with validation
 */
export async function putValidated<T>(
  schema: z.ZodSchema<T>,
  url: string,
  body: unknown,
  options: ValidatedFetchOptions = {},
): Promise<ValidationResult<T>> {
  return fetchValidated(schema, url, {
    ...options,
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/**
 * DELETE request (no validation needed for response)
 */
export async function deleteValidated(
  url: string,
  options: ValidatedFetchOptions = {},
): Promise<ValidationResult<void>> {
  const { jwt, context, logErrors, ...fetchOptions } = options;

  try {
    const headers = new Headers(fetchOptions.headers);
    if (jwt) {
      headers.set("Authorization", `Bearer ${jwt}`);
    }

    const response = await fetch(url, {
      ...fetchOptions,
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    return {
      success: true,
      data: undefined,
    };
  } catch (error) {
    const errorMessage = context
      ? `Failed to delete ${context}: ${error instanceof Error ? error.message : "Unknown error"}`
      : `Delete failed: ${error instanceof Error ? error.message : "Unknown error"}`;

    if (logErrors !== false) {
      console.error("[Validated Fetch]", errorMessage, error);
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
}
